import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "../../lib/auth";
import { AVAILABILITY_RETURN_PATH, clearAvailabilityDraft, readAvailabilityDraft, saveAvailabilityDraft } from "../../lib/availabilityRecovery";
import { CalendarClock } from "lucide-react";
import { useReducedMotion } from "motion/react";
import { api, ApiError } from "../../lib/api";
import type { Availability } from "../../lib/api-client/profiles";
import { useI18n } from "../../lib/i18n";
import { DayRow } from "../../components/motion/availability-scheduler/day-row";
import { buildOptions, WEEKDAYS, type DayKey, type WeekAvailability } from "../../components/motion/availability-scheduler/types";
import "./AvailabilityEditor.css";

const DAY_NUMBERS: Record<DayKey, number> = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
const TIME_OPTIONS = buildOptions(1);

function weekFromSlots(slots: Availability["slots"]): WeekAvailability {
  return Object.fromEntries(WEEKDAYS.map(({ key }) => {
    const ranges = slots.filter(slot => slot.dayOfWeek === DAY_NUMBERS[key])
      .map(slot => ({ id: slot.id, start: slot.startTime, end: slot.endTime }));
    return [key, { enabled: ranges.length > 0, ranges }];
  })) as WeekAvailability;
}

export function AvailabilityEditor() {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const [, navigate] = useLocation();
  const [recovered, setRecovered] = useState(() => user ? readAvailabilityDraft(user) : null);
  const pendingRecovery = useRef(recovered);
  const [draftStorageFailed, setDraftStorageFailed] = useState(false);
  const mounted = useRef(false);
  const recoveredSlotIds = useRef<string[] | null>(null);
  const reduce = useReducedMotion() ?? false;
  const [saved, setSaved] = useState<Availability | null>(null);
  const [week, setWeek] = useState<WeekAvailability | null>(null);
  const [savedWeek, setSavedWeek] = useState<WeekAvailability | null>(null);
  const [day, setDay] = useState<DayKey>("mon");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [signInRequired, setSignInRequired] = useState(false);
  const dirty = JSON.stringify(week) !== JSON.stringify(savedWeek);

  function discardRecovery() {
    // A late initial read must not restore hours discarded while it was in flight.
    pendingRecovery.current = null;
    clearAvailabilityDraft(); setRecovered(null); recoveredSlotIds.current = null;
  }
  function acceptLoaded(value: Availability, restore = false) {
    const recovery = pendingRecovery.current;
    setSaved(value);
    const loadedWeek = weekFromSlots(value.slots);
    setSavedWeek(loadedWeek);
    if (restore && recovery) {
      if (recovery.week) {
        setWeek(recovery.week); setDay(recovery.day!);
        recoveredSlotIds.current = recovery.savedSlotIds!;
      } else {
        const restored = structuredClone(loadedWeek);
        for (const dayOfWeek of recovery.days) {
          const key = WEEKDAYS.find(item => DAY_NUMBERS[item.key] === dayOfWeek)!.key;
          restored[key] = { enabled: true, ranges: [...restored[key].ranges,
            { id: crypto.randomUUID(), start: recovery.start, end: recovery.end }] };
        }
        setWeek(restored);
        if (recovery.days.length) setDay(WEEKDAYS.find(item => DAY_NUMBERS[item.key] === recovery.days[0])!.key);
      }
    } else setWeek(loadedWeek);
  }
  function showError(failure: unknown) {
    if (!mounted.current || (failure instanceof DOMException && failure.name === "AbortError")) return;
    const expired = failure instanceof ApiError && failure.status === 401;
    setSignInRequired(expired);
    setError(expired ? "Your sign-in expired. Sign in again, then retry."
      : failure instanceof Error ? failure.message : "Could not save availability. Please retry.");
  }
  useEffect(() => {
    mounted.current = true;
    api.myAvailability().then(value => { if (mounted.current) acceptLoaded(value, true); })
      .catch(showError);
    return () => { mounted.current = false; };
  }, []);

  async function reload() {
    setBusy(true); setError(""); setMessage("");
    try {
      const result = await api.myAvailability();
      if (mounted.current) {
        const restore = !saved && !!recovered;
        acceptLoaded(result, restore);
        if (!restore) discardRecovery();
        setSignInRequired(false);
      }
    }
    catch (failure: unknown) { showError(failure); }
    finally { if (mounted.current) setBusy(false); }
  }
  async function setStatus(status: "available" | "busy" | "unavailable") {
    setBusy(true); setError(""); setMessage("");
    try {
      const result = await api.setAvailabilityStatus(status);
      if (!mounted.current) return;
      setSaved(current => current ? { ...current, status: result.status } : current);
      setSignInRequired(false);
    } catch (failure: unknown) { showError(failure); }
    finally { if (mounted.current) setBusy(false); }
  }
  async function save() {
    if (!week || !saved || busy) return;
    const slots = WEEKDAYS.flatMap(({ key }) => week[key].enabled
      ? week[key].ranges.map(range => ({ dayOfWeek: DAY_NUMBERS[key], startTime: range.start, endTime: range.end })) : []);
    if (slots.some(slot => !/^([01]\d|2[0-3]):[0-5]\d$/.test(slot.startTime)
      || !/^([01]\d|2[0-3]):[0-5]\d$/.test(slot.endTime) || slot.startTime >= slot.endTime)) {
      setError("Choose a valid start and end time for each range. Start must be before end."); return;
    }
    if (new Set(slots.map(slot => `${slot.dayOfWeek}:${slot.startTime}:${slot.endTime}`)).size !== slots.length) {
      setError("Remove duplicate time ranges before saving."); return;
    }
    setBusy(true); setError(""); setMessage("");
    try {
      const result = await api.saveAvailabilitySchedule(slots, recoveredSlotIds.current ?? saved.slots.map(slot => slot.id));
      if (!mounted.current) return;
      setSaved(result); setSavedWeek(structuredClone(week));
      setSignInRequired(false); setMessage("Hours saved."); discardRecovery();
    } catch (failure: unknown) { showError(failure); }
    finally { if (mounted.current) setBusy(false); }
  }
  function signIn() {
    if (!user) return;
    // Restore only this owner's typed draft; signing in never replays a save or status change.
    const retained = saveAvailabilityDraft(user, week && saved
      ? { days: [], start: "", end: "", week, day, savedSlotIds: recoveredSlotIds.current ?? saved.slots.map(slot => slot.id) }
      : recovered ?? { days: [], start: "", end: "" });
    if (!retained && !draftStorageFailed) {
      setDraftStorageFailed(true); setError("Could not keep your hours on this device. Copy them before continuing to sign in."); return;
    }
    setUser(null);
    navigate(retained ? "/login?returnTo=" + encodeURIComponent(AVAILABILITY_RETURN_PATH) : "/login");
  }
  function discardDraft() {
    if (savedWeek) setWeek(structuredClone(savedWeek));
    discardRecovery(); setError(""); setMessage("");
  }
  function copyDay(targets: DayKey[]) {
    if (!week) return;
    const next = structuredClone(week);
    for (const target of targets) next[target] = { enabled: week[day].enabled,
      ranges: week[day].ranges.map(range => ({ ...range, id: crypto.randomUUID() })) };
    setWeek(next); setMessage("");
  }

  return <section className="worker-profile-section worker-profile-availability card p-5 mt-4">
    <h3 className="font-bold text-navy mb-3 flex items-center gap-2"><CalendarClock className="w-4 h-4" />{t("profile.availability")}</h3>
    {recovered && (recovered.week || recovered.days.length > 0 || recovered.start || recovered.end) && <div className="mb-3 text-sm text-mut"><p role="status">Your unsaved hours are restored. Review them before saving.</p><button type="button" disabled={busy} onClick={discardDraft} className="font-semibold underline">Discard draft</button></div>}
    {!saved || !week ? <>{!error && <p role="status" className="text-sm text-mut">Loading availability…</p>}
      {error && <><p role="alert" className="text-sm text-brand-red">{error}</p><button type="button" className="btn-outline mt-3" disabled={busy} onClick={() => void reload()}>Retry</button></>}</> : <>
      <fieldset disabled={busy} className="availability-fieldset">
        <div className="worker-profile-status flex flex-wrap gap-2 mb-4" role="group" aria-label="Availability for new work">
          {(["available", "unavailable"] as const).map(status => <button type="button" key={status} aria-pressed={saved.status === status || status === "unavailable" && saved.status === "busy"}
            onClick={() => void setStatus(status)} className={`chip ${(saved.status === status || status === "unavailable" && saved.status === "busy")
              ? { available: "bg-emerald-100 text-emerald-700", busy: "bg-amber-100 text-amber-700", unavailable: "bg-navy-50 text-mut" }[status] + " ring-1 ring-navy/20"
              : "bg-white border border-line text-mut hover:text-navy"}`}>{t(`profile.status.${status}`)}</button>)}
        </div>
        <h4 className="text-sm font-bold text-navy mb-3">Weekly hours</h4>
        <div className="availability-day-tabs" role="tablist" aria-label="Choose a weekday">
          {WEEKDAYS.map(({ key, label }, index) => <button key={key} type="button" id={`availability-day-${key}`} role="tab"
            aria-selected={day === key} aria-controls="availability-day-panel" tabIndex={day === key ? 0 : -1}
            data-availability={week[key].enabled ? "available" : "unavailable"} onClick={() => setDay(key)} onKeyDown={event => {
              if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
              event.preventDefault();
              const next = event.key === "Home" ? 0 : event.key === "End" ? 6 : (index + (event.key === "ArrowRight" ? 1 : -1) + 7) % 7;
              setDay(WEEKDAYS[next].key); document.getElementById(`availability-day-${WEEKDAYS[next].key}`)?.focus();
            }}><span>{label.slice(0, 3)}</span><small>{week[key].enabled ? "Available" : "Not available"}</small></button>)}
        </div>
        <div id="availability-day-panel" className="availability-day-editor" role="tabpanel" aria-labelledby={`availability-day-${day}`}>
          <DayRow key={day} day={day} label={WEEKDAYS.find(item => item.key === day)!.label} state={week[day]} options={TIME_OPTIONS}
            reduce={reduce} elevated={false} openPanel={null} onPanelOpenChange={() => undefined}
            onChange={next => { setWeek({ ...week, [day]: next }); setMessage(""); }} onCopy={copyDay} />
        </div>
      </fieldset>
      {error && <p role="alert" className="text-sm text-brand-red mt-3">{error}</p>}
      {message && <p role="status" className="text-sm text-brand-greendark mt-3">{message}</p>}
      <div className="availability-save-actions">
        <button type="button" className="btn-outline" disabled={busy || !dirty} onClick={discardDraft}>Discard changes</button>
        <button type="button" className="btn-red" disabled={busy || !dirty} onClick={() => void save()}>{busy ? "Saving…" : "Save hours"}</button>
        {error && <button type="button" className="btn-ghost" disabled={busy} onClick={() => void reload()}>Discard draft and reload</button>}
      </div>
    </>}
    {signInRequired && <button type="button" className="btn-outline mt-3" onClick={signIn}>{draftStorageFailed ? "Sign in without keeping hours" : "Sign in"}</button>}
  </section>;
}
